import * as matchOperations from "./operations";
import {
  APIClinicianTag,
  APIConflictOfInterest,
  BasicMatch,
  BasicUser,
  CCVRData,
  ClinicianMatchTag,
  ClinicianTagPreference,
  ClinicPreference,
  ConsultExtended,
  ConsultNPSForm,
  ConsultNPSFormWithScores,
  ExtendedMatch,
  Fit,
  IdMap,
  MatchPriority,
  MatchSlotPreference,
  MatchTag,
  Tag,
} from "../../../api/types";
import {
  actionFactory,
  ActionUnion,
  payloadAction,
  simpleAction,
} from "reductser";
import produce from "immer";
import { assertUnreachable } from "../types";
import { uniqBy } from "lodash-es";
import { ClinicianSlotData } from "../../../app/consults-and-matching/ClinicianSlotTool/types";
import {
  getFeedbackAvgScoreFromForm,
  getNpsScoreFromForm,
  toMap,
} from "../../../app/_helpers";

interface LoadingMap {
  [id: number]: boolean;
}
export type ClinicianSubset = "all" | "cuser" | "my_team" | "clinician";
export interface MyConsultsFilter {
  subset: ClinicianSubset;
  clinicianId: number | null;
  userId: number | null;
}

const actionMap = {
  createClinicianTag: payloadAction<APIClinicianTag>(),
  deleteClinicianTag: payloadAction<{ clinicianId: number; tagId: number }>(),
  fetchingExtendedMatch: payloadAction<LoadingMap>(),
  fetchingMyMatches: payloadAction<boolean>(),
  loadAssignableUsers: payloadAction<BasicUser[]>(),
  loadInclusiveCategories: payloadAction<string[]>(),
  loadTags: payloadAction<Tag[]>(),
  patchClinicianTag: payloadAction<{
    clinicianId: number;
    prefer_not_to_treat: boolean;
    preference: ClinicianTagPreference;
    tagId: number;
    weight: -1 | 1 | 2 | 4;
  }>(),
  saveClinicianTags: payloadAction<APIClinicianTag[]>(),
  saveConsultNPSForms: payloadAction<ConsultNPSForm[]>(),
  saveExtendedMatch: payloadAction<ExtendedMatch>(),
  saveMyMatches: payloadAction<BasicMatch[]>(),
  setCCVRData: payloadAction<CCVRData>(),
  setClinicianSlotInfo: payloadAction<{
    clinicianId: number;
    clinicianSlotInfo: ClinicianSlotData;
  }>(),
  setConsultTags: payloadAction<IdMap<MatchTag[]>>(),
  setEmailModalMatch: payloadAction<BasicMatch | undefined>(),
  setLoadingClinicianTags: payloadAction<boolean>(),
  setMatchClinicPreferences: payloadAction<IdMap<ClinicPreference[]>>(),
  setMatchConflictsOfInterest: payloadAction<IdMap<APIConflictOfInterest[]>>(),
  setMatchFits: payloadAction<IdMap<Fit[]>>(),
  setMatchPriority: payloadAction<{
    matchId: number;
    priority: MatchPriority;
  }>(),
  setMatchReturningToSameClinician: payloadAction<{
    matchId: number;
    returningToSameClinician: boolean;
  }>(),
  setMatchSlotPreferences: payloadAction<{
    [id: number]: MatchSlotPreference[];
  }>(),
  setMatchTags: payloadAction<IdMap<MatchTag[]>>(),
  setMyConsultsFilter: payloadAction<MyConsultsFilter>(),
  toggleMatchIsFlagged: payloadAction<number>(),
  updateMatchAssignees: payloadAction<{
    matchIds: number[];
    assigneeId: number;
  }>(),
  updateMatchInMyMatches: payloadAction<BasicMatch>(),
};

export const matchActions = actionFactory(actionMap, "MATCHES");

export type MatchActions = ActionUnion<typeof matchActions>;

export interface MatchState {
  assignableUsers: BasicUser[];
  ccvrData: CCVRData;
  clinicianSlotInfoMap: { [clinicianId: number]: ClinicianSlotData };
  clinicianTags: APIClinicianTag[];
  consultNPSFormMap: { [match_id: number]: ConsultNPSFormWithScores };
  consultTagsMap: IdMap<MatchTag[]>;
  emailModalMatch?: BasicMatch;
  extendedMatchLoadingMap: LoadingMap;
  extendedMatchMap: { [id: number]: ExtendedMatch };
  fetchingMyMatches: boolean;
  flaggedMatches: { [id: number]: boolean };
  inclusiveCategories: string[];
  loadingClinicianTags: boolean;
  matchClinicPreferencesMap: IdMap<ClinicPreference[]>;
  matchConflictsOfInterestMap: IdMap<APIConflictOfInterest[]>;
  matchFitsMap: { [id: number]: Fit[] };
  matchSlotPreferenceMap: { [id: number]: MatchSlotPreference[] };
  matchTagsMap: IdMap<MatchTag[]>;
  myConsultsFilter: MyConsultsFilter;
  myMatchesMap: IdMap<BasicMatch>;
  tagMap: { [id: number]: Tag };
  tags: Tag[];
}

function getInitialState(): MatchState {
  return {
    assignableUsers: [],
    ccvrData: {},
    clinicianSlotInfoMap: {},
    clinicianTags: [],
    consultNPSFormMap: {},
    consultTagsMap: {},
    emailModalMatch: undefined,
    extendedMatchLoadingMap: {},
    extendedMatchMap: {},
    fetchingMyMatches: false,
    flaggedMatches: {},
    inclusiveCategories: [],
    loadingClinicianTags: false,
    matchClinicPreferencesMap: {},
    matchConflictsOfInterestMap: {},
    matchFitsMap: {},
    matchSlotPreferenceMap: {},
    matchTagsMap: {},
    myConsultsFilter: {
      subset: "cuser",
      clinicianId: null,
      userId: null,
    },
    myMatchesMap: {},
    tagMap: {},
    tags: [],
  };
}

const reducer = (state = getInitialState(), action: MatchActions) =>
  produce(state, (draft) => {
    if (action.reducer === "MATCHES") {
      switch (action.type) {
        case "createClinicianTag":
          draft.clinicianTags = uniqBy(
            draft.clinicianTags.concat([action.payload]),
            "id",
          );
          return;
        case "deleteClinicianTag":
          draft.clinicianTags = draft.clinicianTags.filter(
            (ct) =>
              !(
                ct.clinician_id === action.payload.clinicianId &&
                ct.tag_id === action.payload.tagId
              ),
          );
          return;
        case "fetchingExtendedMatch":
          draft.extendedMatchLoadingMap = {
            ...draft.extendedMatchLoadingMap,
            ...action.payload,
          };
          return;
        case "fetchingMyMatches":
          draft.fetchingMyMatches = action.payload;
          return;
        case "loadAssignableUsers":
          draft.assignableUsers = action.payload;
          return;
        case "loadInclusiveCategories":
          draft.inclusiveCategories = action.payload;
          return;
        case "loadTags":
          draft.tags = action.payload;
          draft.tagMap = toMap(action.payload);
          return;
        case "patchClinicianTag":
          draft.clinicianTags = draft.clinicianTags.map((ct) =>
            ct.clinician_id === action.payload.clinicianId &&
            ct.tag_id === action.payload.tagId
              ? {
                  ...ct,
                  weight: action.payload.weight,
                  prefer_not_to_treat: action.payload.prefer_not_to_treat,
                  preference: action.payload.preference,
                }
              : ct,
          );
          return;
        case "saveClinicianTags":
          draft.clinicianTags = uniqBy(
            draft.clinicianTags.concat(action.payload),
            "id",
          );
          return;
        case "saveConsultNPSForms":
          action.payload.forEach((form) => {
            draft.consultNPSFormMap[form.match_id] = {
              form,
              nps: getNpsScoreFromForm(form),
              feedbackAvg: getFeedbackAvgScoreFromForm(form),
            };
          });
          return;
        case "saveExtendedMatch":
          draft.extendedMatchMap[action.payload.id] = action.payload;
          return;
        case "saveMyMatches":
          draft.myMatchesMap = { ...draft.myMatchesMap };
          action.payload.forEach((match) => {
            draft.myMatchesMap[match.id] = match;
          });
          return;
        case "setCCVRData":
          draft.ccvrData = action.payload;
          return;
        case "setClinicianSlotInfo":
          draft.clinicianSlotInfoMap[action.payload.clinicianId] =
            action.payload.clinicianSlotInfo;
          return;
        case "setConsultTags":
          draft.consultTagsMap = {
            ...draft.consultTagsMap,
            ...action.payload,
          };
          return;
        case "setEmailModalMatch":
          draft.emailModalMatch = action.payload;
          return;
        case "setLoadingClinicianTags":
          draft.loadingClinicianTags = action.payload;
          return;
        case "setMatchClinicPreferences":
          draft.matchClinicPreferencesMap = {
            ...draft.matchClinicPreferencesMap,
            ...action.payload,
          };
          return;
        case "setMatchConflictsOfInterest":
          draft.matchConflictsOfInterestMap = {
            ...draft.matchConflictsOfInterestMap,
            ...action.payload,
          };
          return;
        case "setMatchFits":
          draft.matchFitsMap = {
            ...draft.matchFitsMap,
            ...action.payload,
          };
          return;
        case "setMatchPriority":
          draft.myMatchesMap[action.payload.matchId] = {
            ...draft.myMatchesMap[action.payload.matchId],
            priority: action.payload.priority,
          };
          return;
        case "setMatchReturningToSameClinician":
          draft.myMatchesMap[action.payload.matchId] = {
            ...draft.myMatchesMap[action.payload.matchId],
            returning_to_same_clinician:
              action.payload.returningToSameClinician,
          };
          return;
        case "setMatchSlotPreferences":
          draft.matchSlotPreferenceMap = {
            ...draft.matchSlotPreferenceMap,
            ...action.payload,
          };
          return;
        case "setMatchTags":
          draft.matchTagsMap = {
            ...draft.matchTagsMap,
            ...action.payload,
          };
          return;
        case "setMyConsultsFilter":
          draft.myConsultsFilter = action.payload;
          return;
        case "toggleMatchIsFlagged":
          draft.flaggedMatches[action.payload] =
            !draft.flaggedMatches[action.payload];
          return;
        case "updateMatchAssignees":
          action.payload.matchIds.forEach((id) => {
            draft.myMatchesMap[id].assignee = action.payload.assigneeId;
          });
          return;
        case "updateMatchInMyMatches":
          draft.myMatchesMap = {
            ...draft.myMatchesMap,
            ...{
              [action.payload.id]: action.payload,
            },
          };
          return;
        default:
          assertUnreachable(action);
      }
    }
  });

export default reducer;
export { matchOperations };
